import { ActionTree, ActionContext } from "vuex";
import { RootState } from "@/store";

import { State } from "./state";
import { Mutations } from "./mutations";
import { DashboardMutationTypes } from "./mutation-types";
import { DashboardActionTypes } from "./action-types";
import moment from "moment";
import api from "@/store/axiosAgsc";

type AugmentedActionContext = {
  commit<K extends keyof Mutations>(
    key: K,
    payload: Parameters<Mutations[K]>[1]
  ): ReturnType<Mutations[K]>;
} & Omit<ActionContext<State, RootState>, "commit">;

export interface Actions {
  [DashboardActionTypes.GET_TOP5_COMPUTERS](
    { commit }: AugmentedActionContext,
    payload: Array<any> // Obsolete in here but left as an example
  ): Promise<Array<any>>;
  [DashboardActionTypes.GET_TOP5_GROUPS](
    { commit }: AugmentedActionContext,
    payload: Array<any> // Obsolete in here but left as an example
  ): Promise<Array<any>>;
  [DashboardActionTypes.GET_EVENT_TRENDS](
    { commit }: AugmentedActionContext,
    payload: any // Obsolete in here but left as an example
  ): Promise<void>;
  [DashboardActionTypes.GET_TOP_COMPUTERS](
    { commit }: AugmentedActionContext,
    payload: any // Obsolete in here but left as an example
  ): Promise<void>;
  [DashboardActionTypes.GET_BLOCKS_OVERTIME](
    { commit }: AugmentedActionContext,
    payload: any // Obsolete in here but left as an example
  ): Promise<void>;
  [DashboardActionTypes.GET_ALERT_SUMMARY](
    { commit }: AugmentedActionContext,
    payload: any // Obsolete in here but left as an example
  ): Promise<Array<any>>;
  [DashboardActionTypes.GET_COMPANY_OVERVIEW](
    { commit }: AugmentedActionContext,
    payload: any // Obsolete in here but left as an example
  ): Promise<Array<any>>;
  [DashboardActionTypes.GET_KNOWN_THREADS](
    { commit }: AugmentedActionContext,
    payload: any // Obsolete in here but left as an example
  ): Promise<Array<any>>;
  [DashboardActionTypes.GET_NUMBER_OF_BLOCKS_BY_TIME](
    { commit }: AugmentedActionContext,
    payload: any // Obsolete in here but left as an example
  ): Promise<void>;
}

export const actions: ActionTree<State, RootState> & Actions = {
  async [DashboardActionTypes.GET_TOP5_COMPUTERS]({ commit }) {
    commit(DashboardMutationTypes.SET_TOP5_COMPUTERS_LOADING, true);

    return new Promise(() => {
      api.get("Dashboard/GetTopComputers", null).then((response) => {

        commit(DashboardMutationTypes.SET_TOP5_COMPUTERS_LOADING, false);
        if(response.data.isSuccess === false){
          commit(DashboardMutationTypes.SET_TOP5_COMPUTERS_ERROR, response.data.message);
        } else {
          commit(DashboardMutationTypes.SET_TOP5_COMPUTERS_ERROR, "");
          commit(
              DashboardMutationTypes.SET_TOP5_COMPUTERS,
              response.data.entityData
          );
        }
      });
    });
  },
  async [DashboardActionTypes.GET_TOP5_GROUPS]({ commit }) {
    commit(DashboardMutationTypes.SET_TOP5_GROUPS_LOADING, true);

    return new Promise(() => {
      api.get("Dashboard/GetTopFiveGroups", null).then((response) => {
        commit(DashboardMutationTypes.SET_TOP5_GROUPS, response.data);
        commit(DashboardMutationTypes.SET_TOP5_GROUPS_LOADING, false);
      });
    });
  },
  async [DashboardActionTypes.GET_EVENT_TRENDS]({ commit }, data) {
    return new Promise<void>((resolve) => {
      api.post("Dashboard/GetEventTrends", data).then((response) => {
        commit(
          DashboardMutationTypes.SET_EVENT_TRENDS,
          response.data.entityData
        );
        resolve();
      });
    });
  },
  async [DashboardActionTypes.GET_TOP_COMPUTERS]({ commit }, data) {
    return new Promise<void>((resolve) => {
      api.post("Dashboard/GetTopComputerBlocks", data).then((response) => {
        commit(
          DashboardMutationTypes.SET_TOP_COMPUTERS,
          response.data.entityData
        );
        resolve();
      });
    });
  },
  [DashboardActionTypes.GET_BLOCKS_OVERTIME]({ commit }, data) {
    return new Promise<void>((resolve) => {
      api.post("/Dashboard/GetBlocksOverTime", data).then((response) => {
        commit(
          DashboardMutationTypes.SET_BLOCKS_OVERTIME,
          response.data.entityData
        );
        resolve();
      });
    });
  },
  async [DashboardActionTypes.GET_ALERT_SUMMARY]({ commit }) {
    commit(DashboardMutationTypes.SET_ALERT_SUMMARY_LOADING, true);

    return new Promise(() => {
      api
        .post("Dashboard/GetAlertSummary", {
          startDate: moment()
            .utc()
            .add(-7, "days")
            .startOf("day")
            .utc()
            .format("YYYY-MM-DDTHH:mm:ss.SSS"),
          endDate: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS"),
        })
        .then((response) => {
          commit(
            DashboardMutationTypes.SET_ALERT_SUMMARY,
            response.data.entityData
          );
          commit(DashboardMutationTypes.SET_ALERT_SUMMARY_LOADING, false);
        });
    });
  },
  async [DashboardActionTypes.GET_COMPANY_OVERVIEW]({ commit }) {
    commit(DashboardMutationTypes.SET_COMPANY_OVERVIEW_LOADING, true);
    return new Promise(() => {
      api
        .get("Dashboard/GetCompanyOverviewInformation", null)
        .then((response) => {
          commit(
            DashboardMutationTypes.SET_COMPANY_OVERVIEW,
            response.data.entityData
          );
          commit(DashboardMutationTypes.SET_COMPANY_OVERVIEW_LOADING, false);
        });
    });
  },
  async [DashboardActionTypes.GET_KNOWN_THREADS]({ commit }) {
    commit(DashboardMutationTypes.SET_KNOWN_THREADS_LOADING, true);

    return new Promise(() => {
      api.get("Dashboard/GetKnownThreads", null).then((response) => {
        commit(
          DashboardMutationTypes.SET_KNOWN_THREADS,
          response.data.entityData
        );
        commit(DashboardMutationTypes.SET_KNOWN_THREADS_LOADING, false);
      });
    });
  },
  async [DashboardActionTypes.GET_NUMBER_OF_BLOCKS_BY_TIME]({ commit }, data) {
    return new Promise<void>((resolve) => {
      api.post("Dashboard/GetNumberOfBlocksByTime", data).then((response) => {
        commit(
          DashboardMutationTypes.SET_NUMBER_OF_BLOCKS_BY_TIME,
          response.data.entityData
        );
        resolve();
      });
    });
  },
};
